/**
 * Collection page orchestration — port of CategoryPageLPClient.tsx:
 *
 * Toolbar choreography (exact):
 * - sentinel IO with rootMargin -navH (118px lg / 82px mobile), threshold 0.
 *   Sticky mode arms once the sentinel leaves the viewport; re-entering
 *   always shows the toolbar.
 * - while sticky: hide on scroll-down, show on scroll-up (500ms transform
 *   handled by CSS via .is-hidden).
 *
 * View toggle: Produkt/Zimmer — client-side only, dispatches
 * `dylys:view-change` on the grid (product-card.js swaps base images).
 *
 * Sort + facets (D3): Section Rendering API fetch, targeted DOM swap —
 * no full navigation. URL state via history.pushState / popstate.
 *
 * Category tiles (F55): the subcategory slider's cards are real anchors whose
 * href Shopify's own filter object generated. Here they are upgraded to the
 * same Section Rendering swap so the shopper stays in the collection. Tile and
 * drawer never diverge because BOTH are derived from the URL — the tiles read
 * it back after every swap, the drawer is re-rendered by Shopify.
 */
(function () {
  'use strict';

  function init(root) {
    if (root.dataset.collectionInit) return;
    root.dataset.collectionInit = '1';

    var sectionId = root.getAttribute('data-section-id');
    var sentinel = root.querySelector('[data-toolbar-sentinel]');
    var toolbar = root.querySelector('[data-collection-toolbar]');
    var zimmerActive = false;

    // ── Toolbar hide/show ────────────────────────────────────────────────────
    var isStickyMode = false;
    var lastScrollY = 0;

    function setVisible(visible) {
      if (toolbar) toolbar.classList.toggle('is-hidden', !visible);
    }

    if (sentinel && toolbar) {
      var navH = window.innerWidth >= 1024 ? 118 : 82;
      var observer = new IntersectionObserver(function (entries) {
        isStickyMode = !entries[0].isIntersecting;
        if (entries[0].isIntersecting) setVisible(true);
      }, { threshold: 0, rootMargin: '-' + navH + 'px 0px 0px 0px' });
      observer.observe(sentinel);

      window.addEventListener('scroll', function () {
        var y = window.scrollY;
        if (isStickyMode) setVisible(y < lastScrollY);
        lastScrollY = y;
      }, { passive: true });
    }

    // ── View toggle ──────────────────────────────────────────────────────────
    function applyView() {
      var grid = root.querySelector('[data-collection-grid]');
      if (grid) {
        grid.dispatchEvent(new CustomEvent('dylys:view-change', {
          bubbles: true,
          detail: { zimmer: zimmerActive }
        }));
      }
    }

    root.querySelectorAll('[data-view]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        zimmerActive = btn.getAttribute('data-view') === 'zimmer';
        root.querySelectorAll('[data-view]').forEach(function (b) {
          b.classList.toggle('is-active', b === btn);
        });
        applyView();
      });
    });

    // ── Category filter tiles (outside this section) ─────────────────────────
    // Re-derive every tile's active state and href from the current URL.
    function syncFilterTiles() {
      var params = new URLSearchParams(window.location.search);
      document.querySelectorAll('a[data-filter-tile]').forEach(function (tile) {
        var param = tile.getAttribute('data-filter-param');
        var value = tile.getAttribute('data-filter-value');
        if (!param || !value) return;

        var current = params.getAll(param);
        var active = current.indexOf(value) !== -1;
        tile.classList.toggle('is-active', active);
        tile.setAttribute('aria-pressed', active ? 'true' : 'false');

        // href always points at the *opposite* state (add, or remove if active)
        var next = new URLSearchParams(params);
        next.delete(param);
        current.forEach(function (v) { if (v !== value) next.append(param, v); });
        if (!active) next.append(param, value);
        var qs = next.toString();
        tile.setAttribute('href', window.location.pathname + (qs ? '?' + qs : ''));
      });
    }

    // ── Grouped pseudo-sorts (Kategorie / Designer) ──────────────────────────
    // Shopify's sort_by cannot group, so these two sort options work client-
    // side: fetch every remaining page of the current result set once, then
    // reorder the cards by data-ptype / data-designer (stable sort keeps the
    // server order — Beliebtheit — inside each group) with heading rows.
    var GROUP_MODES = {
      'group:kollektion': 'data-serie',
      'group:designer': 'data-designer'
    };

    function currentGroupMode() {
      var g = new URLSearchParams(window.location.search).get('group_by');
      return g && GROUP_MODES['group:' + g] ? 'group:' + g : null;
    }

    function applyGrouping(mode) {
      var grid = root.querySelector('[data-collection-grid]');
      var attr = GROUP_MODES[mode];
      if (!grid || !attr) return;

      var pages = parseInt(grid.getAttribute('data-pages') || '1', 10);
      var merged = grid.getAttribute('data-merged') === '1';
      var otherLabel = grid.getAttribute('data-group-other-label') || 'Weitere';

      grid.classList.add('is-grouping');
      grid.setAttribute('aria-busy', 'true');

      var fetches = [];
      if (!merged && pages > 1) {
        var base = new URLSearchParams(window.location.search);
        base.delete('group_by');
        base.delete('page');
        base.delete('section_id');
        for (var p = 2; p <= pages; p++) {
          var q = new URLSearchParams(base);
          q.set('page', String(p));
          q.set('section_id', sectionId);
          fetches.push(
            fetch(window.location.pathname + '?' + q.toString())
              .then(function (res) { return res.text(); })
              .catch(function () { return ''; })
          );
        }
      }

      Promise.all(fetches).then(function (docs) {
        docs.forEach(function (html) {
          if (!html) return;
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var extra = doc.querySelector('[data-collection-grid]');
          if (!extra) return;
          extra.querySelectorAll('.product-card-wrap').forEach(function (card) {
            grid.appendChild(document.importNode(card, true));
          });
        });
        grid.setAttribute('data-merged', '1');

        grid.querySelectorAll('[data-group-header]').forEach(function (h) { h.remove(); });
        var cards = Array.prototype.slice.call(grid.querySelectorAll('.product-card-wrap'));
        var keyOf = function (el) { return (el.getAttribute(attr) || '').trim(); };
        cards.sort(function (a, b) {
          var ka = keyOf(a);
          var kb = keyOf(b);
          if (ka === kb) return 0;
          if (!ka) return 1; // cards without a group value go last
          if (!kb) return -1;
          return ka.localeCompare(kb, 'de');
        });

        var lastLabel = null;
        cards.forEach(function (card) {
          var label = keyOf(card) || otherLabel;
          if (label !== lastLabel) {
            var head = document.createElement('h2');
            head.className = 'collection-grid__group';
            head.setAttribute('data-group-header', '');
            head.textContent = label;
            grid.appendChild(head);
            lastLabel = label;
          }
          grid.appendChild(card);
        });

        var pagination = root.querySelector('.collection-pagination');
        if (pagination) pagination.hidden = true;

        if (window.Dylys && window.Dylys.initProductCards) {
          window.Dylys.initProductCards(grid);
        }
        if (zimmerActive) applyView();
        grid.classList.remove('is-grouping');
        grid.removeAttribute('aria-busy');
      });
    }

    // ── Section Rendering fetch + targeted swap ──────────────────────────────
    function fetchAndSwap(paramString, push) {
      var query = paramString ? paramString + '&' : '';
      var url = window.location.pathname + '?' + query + 'section_id=' + sectionId;

      fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');

          [
            '[data-collection-grid]',
            '[data-collection-count]',
            '[data-facet-groups]',
            '[data-facet-results]',
            '[data-filter-count]'
          ].forEach(function (selector) {
            var oldEl = root.querySelector(selector);
            var newEl = doc.querySelector(selector);
            if (oldEl && newEl) oldEl.innerHTML = newEl.innerHTML;
          });

          // Filter button active state + reset button disabled state
          var oldFilterBtn = root.querySelector('[data-facet-open]');
          var newFilterBtn = doc.querySelector('[data-facet-open]');
          if (oldFilterBtn && newFilterBtn) {
            oldFilterBtn.classList.toggle('is-active', newFilterBtn.classList.contains('is-active'));
          }
          var oldReset = root.querySelector('[data-facet-reset]');
          var newReset = doc.querySelector('[data-facet-reset]');
          if (oldReset && newReset) oldReset.disabled = newReset.disabled;

          // Re-bind swapped grid content and keep the current view mode
          var grid = root.querySelector('[data-collection-grid]');
          if (grid && window.Dylys && window.Dylys.initProductCards) {
            window.Dylys.initProductCards(grid);
          }
          if (zimmerActive) applyView();

          if (push) {
            var cleanUrl = window.location.pathname + (paramString ? '?' + paramString : '');
            history.pushState({ dylysCollection: true }, '', cleanUrl);
          }
          syncFilterTiles();

          // The swap resets the grid to server page 1 — leave grouped state,
          // then re-apply it when the URL still asks for a grouped sort.
          var swappedGrid = root.querySelector('[data-collection-grid]');
          if (swappedGrid) swappedGrid.removeAttribute('data-merged');
          var pagination = root.querySelector('.collection-pagination');
          if (pagination) pagination.hidden = false;
          var groupMode = currentGroupMode();
          if (groupMode) {
            if (sortSelect) sortSelect.value = groupMode;
            applyGrouping(groupMode);
          }
        })
        .catch(function () {
          // Network failure — fall back to a full navigation
          window.location.search = paramString || '';
        });
    }

    // ── Sort ─────────────────────────────────────────────────────────────────
    var sortSelect = root.querySelector('[data-sort-select]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var params = new URLSearchParams(window.location.search);
        if (GROUP_MODES[sortSelect.value]) {
          params.delete('page');
          params.set('group_by', sortSelect.value.slice(6));
          var url = window.location.pathname + '?' + params.toString();
          history.pushState({ dylysCollection: true }, '', url);
          applyGrouping(sortSelect.value);
        } else {
          params.delete('group_by');
          params.set('sort_by', sortSelect.value);
          fetchAndSwap(params.toString(), true);
        }
      });
    }

    // ── Facets ───────────────────────────────────────────────────────────────
    root.addEventListener('dylys:facet-change', function (e) {
      fetchAndSwap(e.detail.params, true);
    });

    var openBtn = root.querySelector('[data-facet-open]');
    var drawer = root.querySelector('[data-facet-drawer]');
    if (openBtn && drawer) {
      openBtn.addEventListener('click', function () {
        if (drawer.open) drawer.open();
      });
    }

    // ── Category tiles → same facet state, no navigation ─────────────────────
    document.addEventListener('click', function (e) {
      var tile = e.target.closest ? e.target.closest('a[data-filter-tile]') : null;
      if (!tile) return;
      // Let the browser handle new-tab / new-window / download intents
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      var href = tile.getAttribute('href') || '';
      var parsed;
      try {
        parsed = new URL(href, window.location.origin);
      } catch (err) {
        return;
      }
      if (parsed.pathname !== window.location.pathname) return; // different collection: navigate

      e.preventDefault();
      var params = new URLSearchParams(parsed.search);
      params.delete('section_id');
      fetchAndSwap(params.toString(), true);

      var top = root.getBoundingClientRect().top + window.scrollY;
      var navH = window.innerWidth >= 1024 ? 118 : 82;
      window.scrollTo({ top: Math.max(0, top - navH), behavior: 'smooth' });
    });

    syncFilterTiles();

    // Deep link / reload with an active grouped sort
    var initialGroup = currentGroupMode();
    if (initialGroup) {
      if (sortSelect) sortSelect.value = initialGroup;
      applyGrouping(initialGroup);
    }

    // ── History ──────────────────────────────────────────────────────────────
    window.addEventListener('popstate', function () {
      var params = new URLSearchParams(window.location.search);
      params.delete('section_id');
      fetchAndSwap(params.toString(), false);
    });
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-collection]').forEach(init);
  }

  initAll(document);

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
